import {
  ProfileTab,
  DefaultsTab,
  CarrierBGATab,
  CommunicationTab,
} from '@/components/advisor-settings';
import { Button } from '@/components/ui/button';
import { PlayIcon } from '@/components/PlayIcon';
import { SubmitButton } from '@/components/FormComponents';
import { TabsContainer, TabItem } from '@/components/TabsContainer';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';

import { CirclePlay } from 'lucide-react';

import { useSelector } from 'react-redux';
import React, { useState, useMemo } from 'react';
import { useEmailTags } from '@/util/useEmailTags';
import { useAdvisorTabs } from '@/util/useAdvisorTabs';
import { useAdvisorProfile } from '@/util/useAdvisorProfile';
import { useAdvisorValidation } from '@/util/useAdvisorValidation';
import { useAdvisorFormHandlers } from '@/util/useAdvisorFormHandlers';

import {
  FormErrorAlert,
  UnsavedChangesDialog,
  selectSessionAdvisor,
  selectAdvisorOrganizations,
  useDisableNumberInputScroll,
} from '@client';

type AdvisorDashboardSettingsProps = {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
};

const SETTINGS_VIDEO_URL = 'https://www.loom.com/embed/236cbebdcfe14fe1bf38f4980f2caeae';

export function AdvisorDashboardSettings({
  hasUnsavedChanges,
  setHasUnsavedChanges,
  showUnsavedDialog,
  setShowUnsavedDialog,
}: AdvisorDashboardSettingsProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const advisor = useSelector(selectSessionAdvisor);
  const advisorOrganizations = useSelector(selectAdvisorOrganizations);

  const {
    advisorState,
    setAdvisorState,
    resetState,
    saveAdvisorProfile,
    missingRequiredFields,
    isLoading,
  } = useAdvisorProfile({ hasUnsavedChanges, setHasUnsavedChanges });

  const { errors, setErrors, validateForm, maxSchedulingLinkDisplayTextLength } =
    useAdvisorValidation();

  const {
    handleAdvisorStateChange,
    handlePhoneNumberChange,
    handleAddressFieldChange,
    handleShouldSendClientEmailChange,
  } = useAdvisorFormHandlers({
    advisorState,
    setAdvisorState,
    setHasUnsavedChanges,
    advisor: advisorState,
  });

  const { activeTab, handleTabChange, handleDialogCancel, handleDialogConfirm } = useAdvisorTabs({
    hasUnsavedChanges,
    setShowUnsavedDialog,
  });

  const emailList = useMemo(
    () => advisorState.additionalRecipientsForIntakeFormSubmissions || [],
    [JSON.stringify(advisorState.additionalRecipientsForIntakeFormSubmissions)],
  );

  const { emailTags, clearEmailWarnings, handleEmailTagsChange, getValidEmails, resetEmailTags } =
    useEmailTags(emailList);

  useDisableNumberInputScroll();

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }

    // Update advisor state with valid emails before saving
    const updatedAdvisorState = {
      ...advisorState,
      additionalRecipientsForIntakeFormSubmissions: getValidEmails(),
    };
    setAdvisorState(updatedAdvisorState);

    const result = saveAdvisorProfile(validateForm);
    if (!result.success) {
      setErrorMessage(result.errorMessage);
      setErrors(result.errors);
    } else {
      setErrorMessage(null);
    }
  };

  const handleReset = () => {
    resetState();
    setErrors({});
    setHasUnsavedChanges(false);
    setErrorMessage(null);
    resetEmailTags(advisor.additionalRecipientsForIntakeFormSubmissions || []);
  };

  // Check which tabs have unsaved changes or errors
  const hasProfileChanges = Boolean(
    errors.advisorFirstName ||
    errors.advisorLastName ||
    errors.organizationName ||
    errors.organizationDisplayName ||
    errors.advisorBusinessPhoneNumber ||
    errors.advisorBusinessAddress ||
    (hasUnsavedChanges &&
      (advisorState.advisorFirstName !== advisor.advisorFirstName ||
        advisorState.advisorLastName !== advisor.advisorLastName ||
        advisorState.organizationName !== advisor.organizationName ||
        advisorState.organizationDisplayName !== advisor.organizationDisplayName ||
        advisorState.advisorBusinessPhoneNumber !== advisor.advisorBusinessPhoneNumber ||
        JSON.stringify(advisorState.advisorBusinessAddress) !==
          JSON.stringify(advisor.advisorBusinessAddress))),
  );

  const hasCommunicationChanges = Boolean(
    errors.schedulingLinkUrl ||
    errors.schedulingLinkDisplayText ||
    (hasUnsavedChanges &&
      (advisorState.schedulingLinkUrl !== advisor.schedulingLinkUrl ||
        advisorState.schedulingLinkDisplayText !== advisor.schedulingLinkDisplayText ||
        advisorState.sendClientResultsLinkSetting !== advisor.sendClientResultsLinkSetting ||
        JSON.stringify(advisorState.additionalRecipientsForIntakeFormSubmissions) !==
          JSON.stringify(advisor.additionalRecipientsForIntakeFormSubmissions))),
  );

  const hasDefaultsChanges = Boolean(
    errors.defaultHealthcareInflationPercent ||
    errors.defaultHealthcareInflationSourceText ||
    errors.defaultHealthcareInflationSourceUrl ||
    (hasUnsavedChanges &&
      (advisorState.defaultHealthcareInflationPercent !==
        advisor.defaultHealthcareInflationPercent ||
        advisorState.defaultHealthcareInflationSourceText !==
          advisor.defaultHealthcareInflationSourceText ||
        advisorState.defaultHealthcareInflationSourceUrl !==
          advisor.defaultHealthcareInflationSourceUrl)),
  );

  // Carrier & BGA tab changes will be tracked by the tab's internal hooks
  const hasCarrierBgaChanges = false;

  const TabLabel = ({ title, hasChanges }: { title: string; hasChanges: boolean }) => (
    <div className="flex items-center gap-2">
      {title}
      {hasChanges && (
        <div
          className="h-2 w-2 rounded-full bg-orange-500"
          title="Unsaved changes"
        />
      )}
    </div>
  );

  const tabs: TabItem[] = [
    {
      value: 'profile',
      label: (
        <TabLabel
          title="Profile"
          hasChanges={hasProfileChanges}
        />
      ),
      content: (
        <ProfileTab
          advisorState={advisorState}
          errors={errors}
          handleAdvisorStateChange={handleAdvisorStateChange}
          handlePhoneNumberChange={handlePhoneNumberChange}
          handleAddressFieldChange={handleAddressFieldChange}
        />
      ),
    },
    {
      value: 'communication',
      label: (
        <TabLabel
          title="Communication"
          hasChanges={hasCommunicationChanges}
        />
      ),
      content: (
        <CommunicationTab
          advisorState={advisorState}
          errors={errors}
          maxSchedulingLinkDisplayTextLength={maxSchedulingLinkDisplayTextLength}
          handleAdvisorStateChange={handleAdvisorStateChange}
          handleShouldSendClientEmailChange={handleShouldSendClientEmailChange}
          emailTags={emailTags}
          handleEmailTagsChange={handleEmailTagsChange}
          setHasUnsavedChanges={setHasUnsavedChanges}
          setAdvisorState={setAdvisorState}
          clearEmailWarnings={clearEmailWarnings}
        />
      ),
    },
    {
      value: 'defaults',
      label: (
        <TabLabel
          title="Defaults"
          hasChanges={hasDefaultsChanges}
        />
      ),
      content: (
        <DefaultsTab
          advisorState={advisorState}
          errors={errors}
          handleAdvisorStateChange={handleAdvisorStateChange}
        />
      ),
    },
    {
      value: 'carrier-bga',
      label: (
        <TabLabel
          title="Carrier & BGA"
          hasChanges={hasCarrierBgaChanges}
        />
      ),
      content: <CarrierBGATab />,
    },
  ];

  return (
    <>
      <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <DashboardPageHeader
          title="Advisor Profile Settings"
          videoPlayer={
            <PlayIcon
              icon={CirclePlay}
              videoUrl={SETTINGS_VIDEO_URL}
              size={28}
            />
          }
        />

        <form
          onSubmit={handleSubmit}
          className="max-w-3xl"
        >
          {/* Warning banner for missing fields */}
          {missingRequiredFields.length > 0 && (
            <div className="mb-6 rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Missing information</h3>
                  <div className="mt-1 text-sm text-yellow-700">
                    <p>
                      Please complete the following required fields:{' '}
                      {missingRequiredFields
                        .map(field =>
                          field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                        )
                        .join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8">
            <TabsContainer
              tabs={tabs}
              defaultValue="profile"
              value={activeTab}
              onValueChange={handleTabChange}
              forceMount={true}
            />
          </div>

          {errorMessage && (
            <FormErrorAlert
              mainErrorMessage={errorMessage}
              className="my-4"
            />
          )}

          {/* Action buttons - hidden for Carrier & BGA tab */}
          {activeTab !== 'carrier-bga' && (
            <div className="mt-8 flex items-center justify-end gap-x-4 border-t border-gray-200 pt-6">
              <Button
                type="button"
                className="px-6 py-3"
                variant="outline"
                onClick={handleReset}
              >
                Cancel
              </Button>
              <SubmitButton
                label="Save all"
                isLoading={isLoading}
                disabled={isLoading || !hasUnsavedChanges}
                className="px-6 py-3 text-base"
              />
            </div>
          )}
        </form>
      </main>

      <UnsavedChangesDialog
        showWarning={showUnsavedDialog}
        handleCancelClose={() => {
          setShowUnsavedDialog(false);
        }}
        handleConfirmClose={() => {
          handleSubmit();
          setShowUnsavedDialog(false);
          setHasUnsavedChanges(false);
          handleDialogConfirm();
        }}
        primaryButtonText="Save and navigate"
        secondaryButtonText="Cancel"
        dialogTitle="Would you like to save your changes?"
        dialogDescription="You're about to leave this page with unsaved changes."
        destructive={false}
      />
    </>
  );
}

export { shouldSendClientEmailRadioOptions } from '../components/advisor-settings';
